var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// TODO: make it smaller gap
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import CalendarSets from 'dayjs-plugin-calendar-sets';
import styled from 'styled-components';
import cx from 'classnames';
import { ChevronLeft, ChevronRight } from 'styled-cssgg';
import { animated, useSpring, useSpringRef, useSprings } from '@react-spring/web';
import { Button } from '../button/button';
import { getRect } from '../tooltip/utils';
import { shadow } from '../theme/shadow';
dayjs.extend(CalendarSets);
const StyledDatePicker = styled(animated.div) `
  @apply p-3 w-72;
  color: var(--text-color-0);
  div.date-picker-weeks {
    @apply font-bold;
  }

  div.date-picker-dates {
    @apply overflow-hidden relative;
  }

  div.date-picker-arrow {
    @apply flex justify-between items-center;

    .date-picker-arrow-btn {
      @apply p-0 flex justify-center items-center;
      aspect-ratio: 1 / 1;
    }
  }

  div.date-picker-rows {
    @apply flex flex-col gap-3;
  }

  div[data-role='date-picker-row'] {
    @apply w-full grid gap-4;
    grid-template-columns: repeat(7, 1fr);
  }

  div[data-role='date-picker-row']:first-child {
    @apply pt-1;
  }

  div[data-role='date-picker-row']:last-child {
    @apply pb-1;
  }

  div[data-role='date-picker-operations'] {
    @apply flex gap-3;

    .date-picker-btn {
      @apply flex-1;
    }
  }
`;
const StyleDay = styled.div `
  @apply text-center cursor-pointer flex items-center justify-center;
  span {
    @apply m-0 w-7 inline-block box-border rounded leading-loose;

    aspect-ratio: 1 / 1;
    line-height: 28px;
    color: ${(props) => props.selected ? 'var(--primary-text-color)' : 'var(--text-color-0)'};
    background-color: ${(props) => (props.selected ? 'var(--primary)' : undefined)};
    box-shadow: ${(props) => (props.selected ? shadow.lg : undefined)};
  }
  &.date-picker-day {
    span {
      &:hover {
        border: ${shadow.primary};
        background-color: var(--primary);
        color: var(--primary-text-color);
      }
    }
  }
`;
const weeks = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
export const DatePickerContent = (_a) => {
    var { onChange } = _a, props = __rest(_a, ["onChange"]);
    const [instance] = useState(dayjs.calendarSets());
    const ref = useSpringRef();
    const datePickerRef = useRef(null);
    const [open, setOpen] = useState(true);
    const [sets, setSets] = useState(instance.current());
    const [styles, api] = useSpring(() => ({
        ref,
        height: 'auto',
    }));
    const { currentDate, currentMonth } = useMemo(() => {
        const currentDate = dayjs().date();
        const currentMonth = dayjs().month();
        if (!props.value) {
            return {
                currentDate,
                currentMonth,
            };
        }
        return {
            currentDate: dayjs(props.value).date(),
            currentMonth: dayjs(props.value).month(),
        };
    }, [props.value]);
    const handleChangeDate = useCallback((value) => {
        onChange === null || onChange === void 0 ? void 0 : onChange({ value });
    }, [onChange]);
    const springs = useSprings(sets.length, sets.map(() => ({ from: { opacity: 0 }, to: { opacity: 1 } })));
    useEffect(() => {
        const { height } = getRect(datePickerRef);
        if (height === 0) {
            return;
        }
        api.start({ height, delay: 100 });
    }, [datePickerRef, api, open, ref]);
    return (React.createElement(StyledDatePicker, { className: cx('date-picker', props.className), "data-role": "date-picker" },
        React.createElement("div", { className: "date-picker-rows", "data-role": "date-picker-rows" },
            React.createElement("div", { className: "date-picker-arrow", "data-role": "date-picker-arrow" },
                React.createElement(Button, { onClick: () => {
                        setOpen((prev) => !prev);
                        instance.prev();
                        setSets(instance.current());
                    }, className: "date-picker-arrow-btn", "data-role": "date-picker-arrow-left" },
                    React.createElement(ChevronLeft, null)),
                React.createElement("span", null, dayjs().month(instance.$month).year(instance.$year).format('YYYY-MM')),
                React.createElement(Button, { onClick: () => {
                        setOpen((prev) => !prev);
                        instance.next();
                        setSets(instance.current());
                    }, className: "date-picker-arrow-btn", "data-role": "date-picker-arrow-right" },
                    React.createElement(ChevronRight, null))),
            React.createElement("div", { className: "date-picker-weeks", "data-role": "date-picker-row" }, weeks.map((day) => {
                return (React.createElement(StyleDay, { key: day },
                    React.createElement("span", null, day)));
            })),
            React.createElement(animated.div, { className: "date-picker-dates", "data-role": "date-picker-dates", style: styles },
                React.createElement("div", { ref: datePickerRef }, springs.map((styles, i) => {
                    return (React.createElement(animated.div, { key: i, style: styles, "data-role": "date-picker-row" }, sets[i].map((day, i) => {
                        const month = dayjs(day).month();
                        const date = dayjs(day).date();
                        return (React.createElement(StyleDay, { onClick: () => handleChangeDate(day), className: cx({ 'date-picker-day': !!day }), selected: month === currentMonth && date === currentDate, key: day || i },
                            React.createElement("span", null, day && dayjs(day).date())));
                    })));
                })))),
        React.createElement("div", { "data-role": "date-picker-operations" },
            React.createElement(Button, { onClick: () => { var _a; return (_a = props.onCancel) === null || _a === void 0 ? void 0 : _a.call(props); }, className: "date-picker-btn", "data-role": "date-picker-btn", size: "lg" }, "CANCEL"),
            React.createElement(Button, { onClick: () => { var _a; return (_a = props.onOk) === null || _a === void 0 ? void 0 : _a.call(props); }, className: "date-picker-btn", "data-role": "data-picker-btn", size: "lg" }, "DONE"))));
};
